import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { House } from "@phosphor-icons/react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import { useI18n } from "@/lib/i18n";

/**
 * Top navigation shared by every public-facing page (landing, login,
 * register, public request, forgot/reset password). Guarantees the user
 * can always click the logo OR the "Accueil" button to reach the
 * homepage, and always shows the language selector.
 *
 * Phase 28.1 — optional `company` prop carries the tenant's public
 * branding doc (shape: `{company_name, branding:{logo_url, primary_color}}`)
 * so branded entry flows (`/:slug/booking` → `/request?company=<slug>`)
 * render the tenant's logo + name + colour instead of the S.DRIVE
 * default. Passing `null`/undefined keeps the historical S.DRIVE
 * header — nothing else in the app needs to change.
 */
export default function PublicHeader({ showLanguageSwitcher = true, langTestId = "public-lang", company = null }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const brand = company?.branding || {};
  const logoUrl  = brand.logo_url || (company ? "" : "/brand/sdrive-logo.png");
  const brandName = company?.company_name || "Sdrive Transfert";
  const homeHref = company?.slug ? `/${company.slug}` : "/";
  // Accent colour used for the "Accueil" button icon. Falls back to the
  // S.DRIVE amber so the S.DRIVE surface is byte-identical to before.
  const accentColor = brand.primary_color || "#F59E0B";
  return (
    <header className="sticky top-0 z-30 border-b border-zinc-800/50 bg-[#050505]/90 backdrop-blur-xl">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-2 sm:gap-3">
        <button
          data-testid="public-header-logo"
          onClick={() => navigate(homeHref)}
          className="flex items-center gap-2 sm:gap-3 min-w-0 focus:outline-none focus:ring-2 focus:ring-amber-500/40 rounded"
          aria-label={`${brandName} — Accueil`}
        >
          {logoUrl ? (
            <img src={logoUrl} alt={brandName} className="h-8 sm:h-9 w-auto" />
          ) : (
            // Tenant with no logo → branded coloured disc with initial.
            <span
              className="h-8 w-8 sm:h-9 sm:w-9 rounded-full shrink-0 flex items-center justify-center text-[13px] sm:text-sm font-semibold"
              style={{ background: accentColor, color: brand.secondary_color || "#0A0A0A" }}
              aria-hidden="true"
            >
              {(brandName.trim()[0] || "?").toUpperCase()}
            </span>
          )}
          <span className="text-base sm:text-lg font-medium text-white truncate">{brandName}</span>
        </button>
        <div className="flex-1" />
        {showLanguageSwitcher && <LanguageSwitcher testid={langTestId} />}
        <ThemeToggle testid={`${langTestId}-theme`} />
        <Button
          data-testid="public-header-home"
          variant="outline"
          size="sm"
          onClick={() => navigate(homeHref)}
          className="border-zinc-700 bg-zinc-900/50 text-zinc-200 hover:bg-zinc-800 hover:text-white h-9 px-3 shrink-0"
        >
          <House size={16} weight="fill" className="sm:mr-1.5" style={{ color: accentColor }} />
          <span className="hidden sm:inline">{t("nav.home")}</span>
        </Button>
      </div>
    </header>
  );
}
